import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Combobox } from '@/components/ui/combobox';
import { WhatsAppLogo, SmsLogo } from '@/components/brand/ChannelLogos';
import { PHONE_OPTS, dialForIso } from '@/lib/countryCodes';
import { useAuth } from '@/hooks/use-auth';
import { homeForUser } from '@/lib/authRoutes';
import { consumePostAuthRedirect } from '@/lib/postAuthRedirect';

/**
 * Phone-number OTP sign-in / sign-up. Sends a code by WhatsApp (default) or
 * SMS, then logs into the account that owns that verified phone — or creates a
 * new phone-first account.
 */
export const PhoneLoginForm = ({ onBeforeSend, termsFooter = null }) => {
  const { requestPhoneOtp, loginWithPhone } = useAuth();
  const navigate = useNavigate();
  const [iso, setIso] = useState('IN');
  const [phone, setPhone] = useState('');
  const [channel, setChannel] = useState('sms');
  const [code, setCode] = useState('');
  const [sent, setSent] = useState(false);
  const [sentInfo, setSentInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const cc = dialForIso(iso);

  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const t = setInterval(() => setResendIn((s) => (s <= 1 ? 0 : s - 1)), 1000);
    return () => clearInterval(t);
  }, [resendIn]);

  const doSend = async () => {
    const digits = phone.replace(/\D/g, '');
    if (digits.length < 7) { toast.error('Enter a valid phone number'); return; }
    setBusy(true);
    try {
      const r = await requestPhoneOtp(digits, cc, channel);
      setSent(true);
      setResendIn(30);
      setSentInfo({ channel: r.channel || channel, to: r.sent_to || `${cc} ${digits}` });
      toast.success(`Code sent via ${r.channel === 'whatsapp' ? 'WhatsApp' : 'SMS'}`, { description: `to ${r.sent_to}` });
    } catch (e) { toast.error('Could not send the code', { description: String(e?.message || e) }); }
    finally { setBusy(false); }
  };

  // Apple UGC gate: SignIn wraps `doSend` in the terms modal when unaccepted.
  const send = () => (onBeforeSend ? onBeforeSend(doSend) : doSend());

  const verify = async () => {
    if (code.trim().length < 4) { toast.error('Enter the 6-digit code'); return; }
    setBusy(true);
    try {
      const data = await loginWithPhone(phone.replace(/\D/g, ''), cc, code.trim());
      toast.success('Signed in');
      navigate(consumePostAuthRedirect() || homeForUser(data?.user), { replace: true });
    } catch (e) { toast.error('Sign-in failed', { description: String(e?.message || e) }); }
    finally { setBusy(false); }
  };

  if (sent) {
    return (
      <div className="mt-7 space-y-4" data-testid="phone-login-otp">
        {sentInfo && (
          <div className="flex items-center gap-2 rounded-full bg-success/10 border border-success/30 px-3 py-1.5 text-[12px] text-success w-fit" data-testid="phone-login-sent-chip">
            {sentInfo.channel === 'whatsapp' ? <WhatsAppLogo className="h-4 w-4" /> : <SmsLogo className="h-4 w-4" />}
            Code sent via {sentInfo.channel === 'whatsapp' ? 'WhatsApp' : 'SMS'} · on its way
          </div>
        )}
        <div>
          <label className="text-[11px] font-grotesk uppercase tracking-[0.18em] text-muted-foreground">Enter code sent to {cc} {phone}</label>
          <Input data-testid="phone-login-code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="6-digit code" inputMode="numeric" className="mt-1.5 h-11 bg-background tracking-[0.4em] text-center text-lg" disabled={busy} />
        </div>
        <Button onClick={verify} disabled={busy} data-testid="phone-login-verify" className="w-full h-11 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 gap-2 font-medium">
          {busy ? 'Verifying…' : <>Verify & sign in <ArrowRight className="h-4 w-4" /></>}
        </Button>
        {sentInfo?.channel === 'whatsapp' && (
          <p className="text-[11px] text-muted-foreground text-center" data-testid="phone-login-wa-hint">
            WhatsApp codes can take up to a minute. Enter the latest code you received — don't request a new one until the timer ends, or the older code stops working.
          </p>
        )}
        <div className="flex items-center justify-between">
          <button type="button" onClick={() => { setSent(false); setCode(''); }} className="text-[11px] font-grotesk uppercase tracking-[0.18em] text-primary hover:underline" data-testid="phone-login-back">
            ← Use a different number
          </button>
          <button type="button" disabled={busy || resendIn > 0} onClick={send} data-testid="phone-login-resend" className="text-[11px] font-grotesk uppercase tracking-[0.18em] text-primary hover:underline disabled:text-muted-foreground disabled:no-underline">
            {resendIn > 0 ? `Resend in ${resendIn}s` : 'Resend code'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-7 space-y-4" data-testid="phone-login-form">
      <div>
        <label className="text-[11px] font-grotesk uppercase tracking-[0.18em] text-muted-foreground">Phone number</label>
        <div className="flex gap-2 mt-1.5">
          <div className="w-[104px] shrink-0">
            <Combobox testid="phone-login-cc" options={PHONE_OPTS} value={iso} onChange={setIso}
              placeholder="Code" searchPlaceholder="Country / code" triggerClassName="h-11 px-2.5 bg-background" />
          </div>
          <Input data-testid="phone-login-number" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="98765 43210" inputMode="tel" className="flex-1 h-11 bg-background" disabled={busy} />
        </div>
      </div>
      <div>
        <label className="text-[11px] font-grotesk uppercase tracking-[0.18em] text-muted-foreground">Send code via</label>
        <div className="grid grid-cols-2 gap-2 mt-1.5">
          {[{ id: 'sms', label: 'SMS', Logo: SmsLogo }, { id: 'whatsapp', label: 'WhatsApp', Logo: WhatsAppLogo }].map(({ id, label, Logo }) => (
            <button key={id} type="button" onClick={() => setChannel(id)} data-testid={`phone-login-channel-${id}`}
              className={`flex items-center justify-center gap-2 h-11 rounded-full border text-sm transition-all ${channel === id ? 'border-primary ring-1 ring-primary bg-primary/5 font-medium' : 'border-border hover:border-primary/40'}`}>
              <Logo className="h-5 w-5" /> {label}
            </button>
          ))}
        </div>
      </div>
      <Button onClick={send} disabled={busy} data-testid="phone-login-send" className="w-full h-11 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 gap-2 font-medium">
        {busy ? 'Sending…' : <>Send me a code <ArrowRight className="h-4 w-4" /></>}
      </Button>
      {termsFooter}
    </div>
  );
};

export default PhoneLoginForm;
